import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CatLoadingIndicator } from '@blocks-react/components/LoadingIndicator';
import { CatIconAddCircle } from '@blocks-react/components/Icons/AddCircle';
import { CatIconStar } from '@blocks-react/components/Icons/Star';
import { CatIconStarFilled } from '@blocks-react/components/Icons/StarFilled';
import { CatIconCopy } from '@blocks-react/components/Icons/Copy';
import { CatIconTrash } from '@blocks-react/components/Icons/Trash';
import { CatIconShare } from '@blocks-react/components/Icons/Share';
import KebabMenu from '@app/components/common/Blocks/KebabMenu';
import { replaceTokensInString } from '@app/utils';
import { commonItemPropType } from '@app/components/pages/account/saved-list/declarations.propTypes';
import {
  DFL,
  FOLSTATUS_Y,
  KEBAB_OPTIONS,
  SHOW,
  TOAST_VARIANT
} from '../../../constants';
import { DEALER_USER_SHARED_LIST_FLAG } from '@app/constants/featureFlags';
import { DEALER } from '@app/constants/affiliations';
import { STATUS } from '@app/constants/commonConstants';
import {
  duplicateSavedListDetails,
  markListFavorite
} from '@app/store/account/actions';
import styles from '../../../savedList.module.scss';

const KebabCell = ({
  item,
  toastOptions,
  onAddToCartClick,
  onDeleteClick,
  onShareListClick
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const showActionDropdownItems = key => item.actions[key] === SHOW;
  const { setToastMessage, clearToastMessage } = toastOptions;

  const className = styles['saved-list-table__kebab-cell-items'];
  const isFavoriteListLimitExceeds = useSelector(
    s => s.account.isFavoriteListLimitExceeds
  );

  const markFavoriteStatus = useSelector(s => s.account.markFavoriteStatus);
  const userAffiliation = useSelector(s => s.common?.userAffiliation);
  const isDealerUser = userAffiliation === DEALER;

  const dealerUserSharedSavedListsFlag = useSelector(
    s => s.featureFlag[DEALER_USER_SHARED_LIST_FLAG]
  );
  const markSavedListFavorite = isFavorite => {
    clearToastMessage();
    const isFavLimitExceededError =
      isFavoriteListLimitExceeds &&
      markFavoriteStatus !== STATUS.REJECTED &&
      t('SAVE_LIST_FAV_MAX_ERROR');

    if (!isFavoriteListLimitExceeds || !isFavorite) {
      dispatch(markListFavorite(item, isFavorite)).then(data => {
        if (!data) {
          return isFavorite
            ? setToastMessage(
                TOAST_VARIANT.ERROR,
                replaceTokensInString(
                  t('SAVE_LIST_FAV_GENERAL_ERROR'),
                  item.name
                )
              )
            : setToastMessage(
                TOAST_VARIANT.ERROR,
                replaceTokensInString(
                  t('SAVE_LIST_FAV_REMOVE_ERROR'),
                  item.name
                )
              );
        }
      });
    } else if (isFavoriteListLimitExceeds) {
      setToastMessage(
        TOAST_VARIANT.ERROR,
        replaceTokensInString(isFavLimitExceededError, item.name)
      );
    }
  };

  const duplicateSavedListAction = () => {
    const folStatus = item.status === DFL ? FOLSTATUS_Y : item.status;
    const duplicatelistName = `${item.name}_2`;
    clearToastMessage();
    dispatch(
      duplicateSavedListDetails(duplicatelistName, item.orderId, folStatus)
    ).then(res => {
      res
        ? setToastMessage(
            TOAST_VARIANT.SUCCESS,
            replaceTokensInString(t('LIST_DUPLICATED_SUCCESSFULLY_'), item.name)
          )
        : setToastMessage(
            TOAST_VARIANT.ERROR,
            replaceTokensInString(
              t('FAILED_LIST_CANNOT_BE_DUPLICATED'),
              item.name
            )
          );
    });
  };

  const kebabMenuItems = [
    {
      id: KEBAB_OPTIONS.ADD_TO_CART,
      icon: <CatIconAddCircle />,
      label: t('REQUISITIONLIST_ACTION_ADD_TO_CART'),
      className,
      onClick: () => onAddToCartClick(item),
      showInMenu: showActionDropdownItems(KEBAB_OPTIONS.ADD_TO_CART)
    },
    {
      id: KEBAB_OPTIONS.MARK_FAVORITE,
      icon: <CatIconStar />,
      label: t('MARK_FAVORITE'),
      className,
      showInMenu: !item.favorite,
      onClick: () => markSavedListFavorite(true)
    },
    {
      id: KEBAB_OPTIONS.MARK_FAVORITE,
      icon: <CatIconStarFilled color="knockout-brand" />,
      label: t('SAVED_LIST_UNFAVORITE'),
      className,
      showInMenu: item.favorite,
      onClick: () => markSavedListFavorite(false)
    },
    {
      id: KEBAB_OPTIONS.DUPLICATE_LIST,
      icon: <CatIconCopy />,
      label: t('REQUISITIONLIST_ACTION_DUPLICATE'),
      className: `text-capitalize ${className}`,
      showInMenu: showActionDropdownItems(KEBAB_OPTIONS.DUPLICATE_LIST),
      onClick: duplicateSavedListAction
    },
    ...(isDealerUser && dealerUserSharedSavedListsFlag
      ? [
          {
            id: KEBAB_OPTIONS.SHARED_LIST,
            icon: <CatIconShare />,
            label: t('REQUISITIONLIST_ACTION_SHARE_LIST'),
            className: `text-capitalize ${className}`,
            onClick: () => onShareListClick(item),
            showInMenu: true
          }
        ]
      : []),
    {
      id: KEBAB_OPTIONS.DELETE_LIST,
      icon: <CatIconTrash />,
      label: t('REQUISITIONLIST_ACTION_DELETE'),
      className: `text-capitalize ${styles['saved-list-table__kebab-cell-delete-items']}`,
      onClick: () => onDeleteClick(item),
      showInMenu: showActionDropdownItems(KEBAB_OPTIONS.DELETE_LIST)
    }
  ];
  return item.isSavedListLineItemLoading ? (
    <CatLoadingIndicator small />
  ) : (
    <KebabMenu menuOptions={kebabMenuItems} position="bottom-center" />
  );
};

KebabCell.propTypes = {
  item: commonItemPropType,
  onAddToCartClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  toastOptions: PropTypes.shape({
    setToastMessage: PropTypes.func,
    clearToastMessage: PropTypes.func
  }),
  onShareListClick: PropTypes.func
};
export default KebabCell;
